// src/hooks/useActivityTracker.js

import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  logGlobalActivity,
  logUserLogin,
  logUserLogout,
  logSearchPerformed,
  logFilterApplied,
  logExportPerformed,
  logError,
  GLOBAL_ACTIVITY_TYPES
} from '../utils/globalActivityLogger';

/**
 * Custom hook for automatic activity tracking
 * Tracks page visits, user interactions, and system events
 */
export const useActivityTracker = () => {
  const { userData, currentUser } = useAuth();
  const sessionStartRef = useRef(null);

  // Track session start/end
  useEffect(() => {
    if (!userData?.companyId || !currentUser) return;

    // Session start
    if (!sessionStartRef.current) {
      sessionStartRef.current = Date.now();
      logUserLogin(userData.companyId, currentUser);
    }

    // Session end (page unload)
    const handleBeforeUnload = () => {
      if (sessionStartRef.current) {
        const sessionDuration = Date.now() - sessionStartRef.current;
        logUserLogout(userData.companyId, currentUser, sessionDuration);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [userData, currentUser]);

  // Activity tracking functions to be used by components
  const trackSearch = (searchTerm, context, resultsCount = 0) => {
    if (!userData?.companyId || !currentUser) return;
    logSearchPerformed(userData.companyId, currentUser, searchTerm, context, resultsCount);
  };

  const trackFilter = (filterType, filterValue, context) => {
    if (!userData?.companyId || !currentUser) return;
    logFilterApplied(userData.companyId, currentUser, filterType, filterValue, context);
  };

  const trackExport = (exportType, entityType, recordCount, format) => {
    if (!userData?.companyId || !currentUser) return;
    logExportPerformed(userData.companyId, currentUser, exportType, entityType, recordCount, format);
  };

  const trackError = (errorType, errorMessage, errorContext) => {
    if (!userData?.companyId || !currentUser) return;
    logError(userData.companyId, currentUser, errorType, errorMessage, errorContext);
  };

  const trackCustomActivity = (activityType, entityType, entityId, entityName, details = {}) => {
    if (!userData?.companyId || !currentUser) return;

    logGlobalActivity({
      companyId: userData.companyId,
      user: currentUser,
      activityType,
      entityType,
      entityId,
      entityName,
      details: {
        ...details,
        userRole: userData?.role || 'kullanıcı'
      }
    });
  };

  const trackEntityView = (entityType, entityId, entityName) => {
    if (!userData?.companyId || !currentUser) return;

    const activityTypeMap = {
      order: GLOBAL_ACTIVITY_TYPES.ORDER_VIEWED,
      customer: GLOBAL_ACTIVITY_TYPES.CUSTOMER_VIEWED,
      product: GLOBAL_ACTIVITY_TYPES.PRODUCT_VIEWED,
      invoice: GLOBAL_ACTIVITY_TYPES.INVOICE_VIEWED,
      supplier: GLOBAL_ACTIVITY_TYPES.SUPPLIER_VIEWED
    };

    const activityType = activityTypeMap[entityType];

    // Only track if it's a valid entity type, don't track page visits
    if (activityType) {
      trackCustomActivity(activityType, entityType, entityId, entityName);
    }
  };

  const trackEntityCreate = (entityType, entityId, entityName, details = {}) => {
    if (!userData?.companyId || !currentUser) return;

    const activityTypeMap = {
      order: GLOBAL_ACTIVITY_TYPES.ORDER_CREATED,
      customer: GLOBAL_ACTIVITY_TYPES.CUSTOMER_CREATED,
      product: GLOBAL_ACTIVITY_TYPES.PRODUCT_CREATED,
      invoice: GLOBAL_ACTIVITY_TYPES.INVOICE_CREATED,
      supplier: GLOBAL_ACTIVITY_TYPES.SUPPLIER_CREATED,
      todo: GLOBAL_ACTIVITY_TYPES.TODO_CREATED,
      transaction: GLOBAL_ACTIVITY_TYPES.TRANSACTION_CREATED
    };

    const activityType = activityTypeMap[entityType];
    if (activityType) {
      trackCustomActivity(activityType, entityType, entityId, entityName, details);
    }
  };

  const trackEntityUpdate = (entityType, entityId, entityName, changes = {}) => {
    if (!userData?.companyId || !currentUser) return;

    const activityTypeMap = {
      order: GLOBAL_ACTIVITY_TYPES.ORDER_UPDATED,
      customer: GLOBAL_ACTIVITY_TYPES.CUSTOMER_UPDATED,
      product: GLOBAL_ACTIVITY_TYPES.PRODUCT_UPDATED,
      invoice: GLOBAL_ACTIVITY_TYPES.INVOICE_UPDATED,
      supplier: GLOBAL_ACTIVITY_TYPES.SUPPLIER_UPDATED,
      todo: GLOBAL_ACTIVITY_TYPES.TODO_UPDATED,
      transaction: GLOBAL_ACTIVITY_TYPES.TRANSACTION_UPDATED,
      settings: GLOBAL_ACTIVITY_TYPES.SETTINGS_UPDATED,
      company: GLOBAL_ACTIVITY_TYPES.COMPANY_INFO_UPDATED
    };

    const activityType = activityTypeMap[entityType];
    if (activityType) {
      trackCustomActivity(activityType, entityType, entityId, entityName, { changes });
    }
  };

  const trackEntityDelete = (entityType, entityId, entityName, reason = '') => {
    if (!userData?.companyId || !currentUser) return;

    const activityTypeMap = {
      order: GLOBAL_ACTIVITY_TYPES.ORDER_DELETED,
      customer: GLOBAL_ACTIVITY_TYPES.CUSTOMER_DELETED,
      product: GLOBAL_ACTIVITY_TYPES.PRODUCT_DELETED,
      invoice: GLOBAL_ACTIVITY_TYPES.INVOICE_DELETED,
      supplier: GLOBAL_ACTIVITY_TYPES.SUPPLIER_DELETED,
      todo: GLOBAL_ACTIVITY_TYPES.TODO_DELETED,
      transaction: GLOBAL_ACTIVITY_TYPES.TRANSACTION_DELETED
    };

    const activityType = activityTypeMap[entityType];
    if (activityType) {
      trackCustomActivity(activityType, entityType, entityId, entityName, { reason });
    }
  };

  const trackReport = (reportType, reportName, filters = {}) => {
    if (!userData?.companyId || !currentUser) return;

    trackCustomActivity(
      GLOBAL_ACTIVITY_TYPES.REPORT_GENERATED,
      'report',
      null,
      reportName,
      { reportType, filters }
    );
  };

  return {
    // Automatic tracking (no need to call these manually)
    // trackPageVisit, trackSessionStart, trackSessionEnd are handled automatically

    // Manual tracking functions for components to use
    trackSearch,
    trackFilter,
    trackExport,
    trackError,
    trackCustomActivity,

    // Entity-specific tracking helpers
    trackEntityView,
    trackEntityCreate,
    trackEntityUpdate,
    trackEntityDelete,
    trackReport,

    // Utility
    isTracking: !!(userData?.companyId && currentUser)
  };
};

export default useActivityTracker;